// Offline write-queue. When fetch fails (network or 5xx), enqueue into IndexedDB.
// On reconnect (window 'online' event) drain queue serially.
// Used by useApi for non-GET requests.

import { pushToast } from './toast.jsx'

const DB_NAME = 'arise-offline'
const STORE   = 'queue'
const VERSION = 1

// Hard caps to prevent the queue from snowballing if a higher-level bug
// (e.g. an effect that fires on every render) starts enqueueing forever.
const MAX_QUEUE_SIZE = 200
const MAX_DRAIN_PER_SESSION = 100

// Paths that have no value being retried — they are best-effort or
// idempotent server-side and have nothing useful to "catch up" on later.
// We refuse to enqueue these on failure (they just drop on the floor).
const NEVER_QUEUE = [
  '/quests/templates/run', // re-runs on next app open
  '/coach/chat',           // chat reply is realtime, stale on replay
  '/coach/chat-stream',
  '/coach/vision',
]

let _db = null
let _drainedThisSession = 0

function openDb() {
  if (_db) return Promise.resolve(_db)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
      }
    }
    req.onsuccess = () => { _db = req.result; resolve(_db) }
    req.onerror   = () => reject(req.error)
  })
}

async function withStore(mode, fn) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const st = tx.objectStore(STORE)
    const res = fn(st)
    tx.oncomplete = () => resolve(res?.result ?? res)
    tx.onerror    = () => reject(tx.error)
  })
}

async function count() {
  const db = await openDb()
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).count()
    req.onsuccess = () => resolve(req.result || 0)
    req.onerror   = () => resolve(0)
  })
}

export async function enqueue(item) {
  // Refuse to queue paths that have no value being retried.
  if (NEVER_QUEUE.some((p) => item.path?.startsWith(p))) return

  const n = await count()
  if (n >= MAX_QUEUE_SIZE) {
    // Queue is full — drop oldest 50 to make room (and to recover from a
    // runaway enqueue loop without snowballing forever).
    await pruneOldest(50)
  }
  await withStore('readwrite', (st) => st.add({ ...item, ts: Date.now() }))
}

async function pruneOldest(n) {
  const db = await openDb()
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite')
    const st = tx.objectStore(STORE)
    const req = st.openCursor()
    let dropped = 0
    req.onsuccess = () => {
      const cur = req.result
      if (!cur || dropped >= n) { resolve(dropped); return }
      cur.delete()
      dropped++
      cur.continue()
    }
    req.onerror = () => resolve(dropped)
  })
}

export async function listQueued() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAll()
    req.onsuccess = () => resolve(req.result || [])
    req.onerror   = () => reject(req.error)
  })
}

export async function removeQueued(id) {
  await withStore('readwrite', (st) => st.delete(id))
}

export async function clearQueue() {
  await withStore('readwrite', (st) => st.clear())
}

export async function drainQueue(fetchFn) {
  if (!navigator.onLine) return { drained: 0, skipped: 0 }

  // Emergency-purge oversized queues from prior bugs so we don't DoS the
  // browser by trying to replay tens of thousands of stale POSTs.
  const total = await count()
  if (total > MAX_QUEUE_SIZE) {
    await clearQueue()
    pushToast({ kind: 'warn', title: 'Offline-Queue zurückgesetzt', body: `${total} alte Aktionen verworfen` })
    return { drained: 0, skipped: total }
  }

  const items = await listQueued()
  let drained = 0, skipped = 0
  for (const it of items) {
    if (_drainedThisSession >= MAX_DRAIN_PER_SESSION) break
    if (NEVER_QUEUE.some((p) => it.path?.startsWith(p))) {
      await removeQueued(it.id)
      skipped++
      continue
    }
    try {
      const res = await fetchFn(it)
      if (res.ok || res.status === 200 || res.status === 201) {
        await removeQueued(it.id)
        drained++
        _drainedThisSession++
      } else if (res.status >= 400 && res.status < 500) {
        // Permanent failure (auth/validation) → drop it
        await removeQueued(it.id)
        skipped++
      }
    } catch { /* network still bad — leave queued */ }
  }
  if (drained > 0) pushToast({ kind: 'success', title: `Sync ✓`, body: `${drained} Aktion${drained > 1 ? 'en' : ''} nachgeholt` })
  return { drained, skipped }
}

export function watchOnline(fetchFn) {
  window.addEventListener('online', () => {
    drainQueue(fetchFn).catch(() => {})
  })
}
